/**
 * Цепочка шагов.
 *
 * Шаг — это уже существующее действие платформы: скан кода, квиз или отметка
 * дня. Новых способов участия цепочка не изобретает, она их выстраивает в
 * порядок — поэтому экран не делает ничего сам, а отправляет на нужный.
 *
 * Пропустить шаг нельзя, и это видно: следующий подсвечен, остальные ждут.
 */
import { ApiError } from '@allswipes/client';
import { Badge, Button, Card, Empty, Icon, Skeleton, num, type IconName } from '@allswipes/ui';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { linkFor } from '../link';
import { Shell } from '../components/Shell';
import { useResource } from '../hooks';

import { useT } from '@allswipes/i18n';

const STEP_ICON: Record<string, IconName> = {
  scan: 'scan',
  quiz: 'list',
  checkin: 'calendar',
};

const STEP_WHERE: Record<string, { label: string; to: (id: string) => string }> = {
  scan: { label: 'Отсканировать код', to: () => '/scan' },
  quiz: { label: 'Пройти квиз', to: (id) => `/c/${id}/quiz` },
  checkin: { label: 'Отметить день', to: (id) => `/c/${id}/streak` },
};

export function QuestScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const view = useResource(() => api.quest(id), [id]);

  if (view.error) {
    return (
      <Shell title={t('Цепочка')}>
        <Empty icon="alert" title={t('Не загрузилось')}>
          {view.error}
        </Empty>
      </Shell>
    );
  }

  const data = view.data;
  const next = data?.steps.find((s) => s.next);

  return (
    <Shell
      title={data?.campaign.name ?? t('Цепочка')}
      subtitle={t('шаги проходятся по порядку — награда за каждый начисляется сразу')}
      action={
        data && (
          <Link to={`/c/${id}`} className="btn out sm">
            {t('К акции')}
          </Link>
        )
      }
    >
      {view.loading && <Skeleton count={3} height={64} />}

      {data && (
        <>
          <Card style={{ marginBottom: 14 }}>
            <div className="row">
              <span className="h2">
                {t('Пройдено {done} из {total}', { done: num(data.done), total: num(data.total) })}
              </span>
              <span className="spacer" />
              {data.finished && <Badge tone="good">{t('цепочка пройдена')}</Badge>}
            </div>
            {data.prize && (
              <p className="sub" style={{ marginTop: 6 }}>
                {data.finished
                  ? t('Приз за цепочку — {prize}. Оформить его можно в «Моих участиях».', {
                      prize: data.prize,
                    })
                  : t('В конце цепочки — {prize}.', { prize: data.prize })}
              </p>
            )}
          </Card>

          <div className="stack">
            {data.steps.map((step) => (
              <Card key={step.index} className={step.next ? 'qstep next' : 'qstep'}>
                <div className="num">{step.done ? <Icon name="check" size={14} /> : step.index + 1}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8 }}>
                    <Icon name={STEP_ICON[step.kind] ?? 'list'} size={14} />
                    <span className="h2" style={{ fontSize: 15 }}>
                      {step.title}
                    </span>
                    {step.reward > 0 && <Badge>+{num(step.reward)}</Badge>}
                  </div>
                  {step.next && (
                    <p className="sub" style={{ marginTop: 6 }}>
                      {t('Это следующий шаг. Остальные откроются после него.')}
                    </p>
                  )}
                </div>
                <span className="spacer" />
                {step.next && (
                  <Button
                    variant="pri"
                    size="sm"
                    onClick={() => navigate(STEP_WHERE[step.kind]?.to(id) ?? `/c/${id}`)}
                  >
                    {t(STEP_WHERE[step.kind]?.label ?? 'Открыть')}
                  </Button>
                )}
              </Card>
            ))}
          </div>

          {!next && !data.finished && (
            <p className="sub" style={{ marginTop: 12 }}>
              {t('Шаги закончились, но цепочка ещё не закрыта — обнови страницу.')}
            </p>
          )}
        </>
      )}
    </Shell>
  );
}

/**
 * Обмен «данные за ценность».
 *
 * Предмет сделки — сами данные, поэтому кода здесь нет. Человек видит
 * значения, которые передаёт, а не названия полей: «ты передаёшь имя» и «ты
 * передаёшь Шах» — разные вещи.
 */
export function DataDealScreen() {
  const t = useT();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const view = useResource(() => api.dataDeal(id), [id]);

  const accept = async () => {
    try {
      await api.acceptDataDeal(id, linkFor(id));
      view.reload();
    } catch (err) {
      if (err instanceof ApiError && err.status === 428) {
        // Не хватает согласия — отправляем туда, где его дают.
        navigate(`/c/${id}`);
        return;
      }
      view.reload();
    }
  };

  const data = view.data;

  return (
    <Shell title={data?.campaign.name ?? t('Обмен')} subtitle={t('данные за ценность — честная сделка')}>
      {view.loading && <Skeleton count={2} height={80} />}
      {view.error && (
        <Empty icon="alert" title={t('Не загрузилось')}>
          {view.error}
        </Empty>
      )}

      {data && (
        <Card>
          <div className="h2">
            {data.prize ? data.prize : t('{n} баллов за анкету', { n: num(data.reward) })}
          </div>
          <p className="sub" style={{ margin: '6px 0 14px' }}>
            {t('Бренд просит данные ниже. Ты видишь, что именно уйдёт — передача добровольна, отказ ничего не стоит, а согласие можно отозвать в профиле.')}
          </p>

          {data.fields.map((f) => (
            <div className="krow" key={f.key}>
              <span className="k">
                {f.label}
                {f.why && (
                  <span className="faint" style={{ display: 'block', fontSize: 11 }}>
                    {f.why}
                  </span>
                )}
              </span>
              <span className="v">{data.values[f.key] || t('— не заполнено')}</span>
            </div>
          ))}

          {data.missing.length > 0 && (
            <p className="sub" style={{ marginTop: 12 }}>
              {t('Часть полей не заполнена — их можно заполнить на экране согласия.')}
            </p>
          )}

          <div style={{ marginTop: 16 }}>
            {data.done ? (
              <Badge tone="good">{t('награда получена')}</Badge>
            ) : (
              <Button variant="pri" block onClick={accept}>
                {t('Передать данные и получить награду')}
              </Button>
            )}
          </div>
        </Card>
      )}
    </Shell>
  );
}
